/**
 * Category Aliases for Voice Command Recognition
 * Organized by language (English and Bengali)
 * Each category maps to a keyword that matches category names in the app
 *
 * Structure:
 * - englishAliases: English keywords and transliterations
 * - bengaliAliases: Bengali native words and transliterations
 * - allAliases: Combined for internal use (built on import)
 */

/**
 * English aliases for category matching
 * keyword → category name substring (lowercase)
 */
export const englishAliases = {
    // ── Food & Drinks ──
    food: 'food',
    drink: 'food',
    drinks: 'food',
    eat: 'food',
    lunch: 'food',
    dinner: 'food',
    breakfast: 'food',
    snack: 'food',
    restaurant: 'food',
    hotel: 'food',
    chips: 'food',
    fries: 'food',
    pizza: 'food',
    burger: 'food',

    // ── Transportation ──
    transport: 'transportation',
    bus: 'transportation',
    uber: 'transportation',
    rickshaw: 'transportation',
    cab: 'transportation',
    taxi: 'transportation',
    ride: 'transportation',

    // ── Vehicle ──
    car: 'vehicle',
    fuel: 'vehicle',
    gas: 'vehicle',
    petrol: 'vehicle',

    // ── Life & Entertainment ──
    entertainment: 'entertainment',
    movie: 'entertainment',
    netflix: 'entertainment',
    fun: 'entertainment',

    // ── Shopping ──
    shop: 'shopping',
    clothes: 'shopping',
    amazon: 'shopping',
    online: 'shopping',

    // ── Housing ──
    rent: 'housing',
    house: 'housing',
    home: 'housing',

    // ── Communication & PC ──
    phone: 'communication',
    mobile: 'communication',
    internet: 'communication',
    wifi: 'communication',
    recharge: 'communication',
    'mobile recharge': 'communication',

    // ── Financial Expense ──
    bank: 'financial',
    charge: 'financial',
    fee: 'financial',

    // ── Investments ──
    invest: 'investment',
    stock: 'investment',
    mutual: 'investment',

    // ── Charity & Donation ──
    donate: 'charity',
    donation: 'charity',
    zakat: 'charity',

    // ── Education & Learning ──
    school: 'education',
    tuition: 'education',
    course: 'education',
    book: 'education',

    // ── Groceries ──
    grocery: 'groceries',
    market: 'groceries',
    vegetable: 'groceries',
    vegetables: 'groceries',

    // ── Healthcare & Medical ──
    doctor: 'healthcare',
    medicine: 'healthcare',
    medical: 'healthcare',
    hospital: 'healthcare',

    // ── Family & Gifts ──
    family: 'family',
    gift: 'gift',
    birthday: 'gift',
    wedding: 'gift',

    // ── Utilities ──
    electric: 'utilities',
    electricity: 'utilities',
    water: 'utilities',
    bill: 'utilities',

    // ── Debt & EMI ──
    emi: 'debt',
    loan: 'debt',
    installment: 'debt',

    // ── Personal Care ──
    salon: 'personal care',
    haircut: 'personal care',
    grooming: 'personal care',

    // ── Alcohol & Tobacco ──
    cigarette: 'alcohol',
    beer: 'alcohol',
    wine: 'alcohol',

    // ── Refund & Cashback ──
    refund: 'refund',
    cashback: 'refund',

    // ── Business & Freelance ──
    freelance: 'business',
    business: 'business',
    client: 'business',

    // ── Bonus & Incentive ──
    bonus: 'bonus',
    incentive: 'bonus',

    // ── Lending & Borrowing ──
    lend: 'lending',
    lending: 'lending',
    borrow: 'borrowed',
    borrowed: 'borrowed',
};

/**
 * Bengali aliases for category matching
 * keyword → category name substring (lowercase)
 * Includes both native Bengali words and transliterated English
 */
export const bengaliAliases = {
    // ── Food & Drinks ──
    // Native Bengali
    খাবার: 'food',
    খাওয়া: 'food',
    রেস্টুরেন্ট: 'food',
    হোটেল: 'food',
    দুপুর: 'food',
    রাতের: 'food',
    সকালের: 'food',
    নাস্তা: 'food',
    চা: 'food',
    খিচুড়ি: 'food',
    // Transliterated English in Bengali script
    ফুড: 'food',
    ড্রিংক: 'food',
    ড্রিংকস: 'food',
    লাঞ্চ: 'food',
    ডিনার: 'food',
    ব্রেকফাস্ট: 'food',
    স্ন্যাক: 'food',
    স্ন্যাকস: 'food',

    // ── Transportation ──
    যাতায়াত: 'transportation',
    ট্রান্সপোর্ট: 'transportation',
    রিকশা: 'transportation',
    বাস: 'transportation',
    সিএনজি: 'transportation',
    অটো: 'transportation',
    ট্রান্সপোর্টেশন: 'transportation',
    উবার: 'transportation',
    ক্যাব: 'transportation',
    ট্যাক্সি: 'transportation',
    রাইড: 'transportation',
    পাঠাও: 'transportation',

    // ── Vehicle ──
    গাড়ি: 'vehicle',
    জ্বালানি: 'vehicle',
    তেল: 'vehicle',
    ভেহিকেল: 'vehicle',
    কার: 'vehicle',
    ফুয়েল: 'vehicle',
    পেট্রোল: 'vehicle',
    ডিজেল: 'vehicle',
    বাইক: 'vehicle',
    মোটরসাইকেল: 'vehicle',

    // ── Life & Entertainment ──
    বিনোদন: 'entertainment',
    এন্টারটেইনমেন্ট: 'entertainment',
    মুভি: 'entertainment',
    নেটফ্লিক্স: 'entertainment',
    ফান: 'entertainment',
    সিনেমা: 'entertainment',
    গেম: 'entertainment',
    খেলা: 'entertainment',

    // ── Shopping ──
    শপিং: 'shopping',
    কেনাকাটা: 'shopping',
    শপ: 'shopping',
    ক্লোথস: 'shopping',
    জামাকাপড়: 'shopping',
    কাপড়: 'shopping',
    অনলাইন: 'shopping',
    আমাজন: 'shopping',
    দারাজ: 'shopping',
    পোশাক: 'shopping', // clothing
    জুতা: 'shopping', // shoes/footwear
    ব্যাগ: 'shopping', // bag
    ঘড়ি: 'shopping', // watch
    গহনা: 'shopping', // jewelry

    // ── Housing ──
    বাড়ি: 'housing',
    ভাড়া: 'housing',
    বাসা: 'housing',
    হাউজিং: 'housing',
    রেন্ট: 'housing',
    হাউস: 'housing',
    হোম: 'housing',

    // ── Communication & PC ──
    মোবাইল: 'communication',
    ইন্টারনেট: 'communication',
    ফোন: 'communication',
    কমিউনিকেশন: 'communication',
    ওয়াইফাই: 'communication',
    রিচার্জ: 'communication',
    ডাটা: 'communication',
    ব্রডব্যান্ড: 'communication',

    // ── Financial Expense ──
    ব্যাংক: 'financial',
    ফাইনান্সিয়াল: 'financial',
    চার্জ: 'financial',
    ফি: 'financial',
    ব্যাংকিং: 'financial',

    // ── Investments ──
    বিনিয়োগ: 'investment',
    ইনভেস্টমেন্ট: 'investment',
    ইনভেস্ট: 'investment',
    স্টক: 'investment',
    মিউচুয়াল: 'investment',
    শেয়ার: 'investment',

    // ── Charity & Donation ──
    দান: 'charity',
    যাকাত: 'charity',
    চ্যারিটি: 'charity',
    ডোনেশন: 'charity',
    ডোনেট: 'charity',
    সাদাকা: 'charity',
    ফিতরা: 'charity',

    // ── Education & Learning ──
    পড়াশোনা: 'education',
    টিউশন: 'education',
    এডুকেশন: 'education',
    স্কুল: 'education',
    কোর্স: 'education',
    বই: 'education',
    কলেজ: 'education',
    ভার্সিটি: 'education',
    ক্লাস: 'education',

    // ── Groceries ──
    বাজার: 'groceries',
    মুদি: 'groceries',
    গ্রসারি: 'groceries',
    গ্রসারিজ: 'groceries',
    মার্কেট: 'groceries',
    সবজি: 'groceries',
    ভেজিটেবল: 'groceries',
    মাছ: 'groceries',
    মাংস: 'groceries',
    চাল: 'groceries',
    ডাল: 'groceries',

    // ── Healthcare & Medical ──
    চিকিৎসা: 'healthcare',
    ডাক্তার: 'healthcare',
    ওষুধ: 'healthcare',
    হাসপাতাল: 'healthcare',
    হেলথকেয়ার: 'healthcare',
    মেডিকেল: 'healthcare',
    মেডিসিন: 'healthcare',
    হসপিটাল: 'healthcare',
    ক্লিনিক: 'healthcare',
    টেস্ট: 'healthcare',
    প্রেসক্রিপশন: 'healthcare',

    // ── Other Expense ──
    'আদার এক্সপেন্স': 'other expense',

    // ── Salary (Income) ──
    সেলারি: 'salary',
    বেতন: 'salary',
    মাইনে: 'salary',
    মাসিক: 'salary',
    পারিশ্রমিক: 'salary',
    স্যালারি: 'salary',

    // ── Business & Freelance (Income) ──
    ব্যবসা: 'business',
    ফ্রিল্যান্স: 'business',
    বিজনেস: 'business',
    ক্লায়েন্ট: 'business',
    প্রজেক্ট: 'business',

    // ── Refund & Cashback (Income) ──
    রিফান্ড: 'refund',
    ক্যাশব্যাক: 'refund',
    ফেরত: 'refund',

    // ── Gifts (Income) ──
    গিফট: 'gift',
    উপহার: 'gift',
    গিফটস: 'gift',
    বার্থডে: 'gift',
    ওয়েডিং: 'gift',
    বিয়ে: 'gift',

    // ── Other Income ──
    'আদার ইনকাম': 'other income',

    // ── Family Expense ──
    পরিবার: 'family',
    ফ্যামিলি: 'family',

    // ── Gifts & Occasions (Expense) ──
    অনুষ্ঠান: 'gift',
    ওকেশন: 'gift',

    // ── Utilities ──
    বিল: 'utilities',
    বিদ্যুৎ: 'utilities',
    গ্যাস: 'utilities',
    পানি: 'utilities',
    ইউটিলিটি: 'utilities',
    ইউটিলিটিজ: 'utilities',
    ইলেকট্রিক: 'utilities',
    ইলেকট্রিসিটি: 'utilities',
    ওয়াটার: 'utilities',

    // ── Debt & EMI Repayment ──
    ঋণ: 'debt',
    কিস্তি: 'debt',
    ইএমআই: 'debt',
    ডেবট: 'debt',
    লোন: 'debt',
    ইনস্টলমেন্ট: 'debt',

    // ── Personal Care ──
    সেলুন: 'personal care',
    প্রসাধনী: 'personal care',
    পার্সোনাল: 'personal care',
    হেয়ারকাট: 'personal care',
    গ্রুমিং: 'personal care',

    // ── Alcohol & Tobacco ──
    মদ: 'alcohol',
    সিগারেট: 'alcohol',
    অ্যালকোহল: 'alcohol',
    বিয়ার: 'alcohol',
    ওয়াইন: 'alcohol',

    // ── Bonus & Incentive (Income) ──
    বোনাস: 'bonus',
    ইনসেন্টিভ: 'bonus',

    // ── Investment Return (Income) ──
    রিটার্ন: 'investment return',
    'ইনভেস্টমেন্ট রিটার্ন': 'investment return',
    লভ্যাংশ: 'investment return',
    ডিভিডেন্ড: 'investment return',

    // ── Lending (Expense) / Borrowed Money (Income) ──
    ধার: 'lending',
    ধারদেনা: 'borrowed',
    লেন্ডিং: 'lending',
    বরো: 'borrowed',
    বরোড: 'borrowed',
    'ধার দিলাম': 'lending',
    'ধার নিলাম': 'borrowed',
};

/**
 * Combined aliases (English + Bengali)
 * Useful for unified alias lookup
 */
export const allAliases = {
    ...englishAliases,
    ...bengaliAliases,
};

/**
 * Get aliases by language
 * @param {string} language - 'en' or 'bn'
 * @returns {Object} Aliases object for the specified language
 */
export const getAliasesByLanguage = (language = 'en') => {
    if (language === 'bn') {
        return bengaliAliases;
    }
    return englishAliases;
};

/**
 * Get all aliases regardless of language
 * Useful when you want to match against all possible aliases
 * @returns {Object} Combined aliases
 */
export const getAllAliases = () => allAliases;

/**
 * Add new category aliases
 * @param {string} language - 'en' or 'bn'
 * @param {Object} newAliases - New aliases to add {keyword: 'category'}
 */
export const addAliases = (language = 'en', newAliases = {}) => {
    if (language === 'bn') {
        Object.assign(bengaliAliases, newAliases);
    } else {
        Object.assign(englishAliases, newAliases);
    }
    // Update combined aliases
    Object.assign(allAliases, newAliases);
};

/**
 * Remove category aliases
 * @param {Array} keywords - Keywords to remove
 */
export const removeAliases = (keywords = []) => {
    keywords.forEach((keyword) => {
        delete englishAliases[keyword];
        delete bengaliAliases[keyword];
        delete allAliases[keyword];
    });
};
